import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, Check, Loader2, Package, X, CheckSquare, MapPin } from 'lucide-react';
import OutboundScanner from '@/components/OutboundScanner';
import { addScanToHistory } from '@/lib/scan-store';
import { API_CONFIG } from '@/shared/constants/api';
import HttpService from '@/shared/services/http.service';
import { Button } from '@/components/ui/button';

import { carrierStyle, PackageData } from '@/lib/packageUtils';
import { PackageDetailsModal } from '@/components/PackageDetailsModal';
import * as Sentry from '@sentry/react';

type FlowStatus = 'scanning' | 'loading' | 'found' | 'not_found' | 'manual_input' | 'error' | 'picked';

const OutboundFlow = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<FlowStatus>('scanning');
  const [packages, setPackages] = useState<PackageData[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedPackage, setSelectedPackage] = useState<PackageData | null>(null);
  const isModalOpen = selectedPackage !== null;
  const [isPicking, setIsPicking] = useState(false);
  const [manualName, setManualName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const manualInputParam = searchParams.get('manual_input');
  const packageIdParam = searchParams.get('packageId');
  const isReturn = searchParams.get('mode') === 'return';

  const location = useLocation();

  const toggleSelect = (id: string) => {
    if (packages.length > 0 && id === packages[0].id) return; // bestMatch is always selected
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (packages.length <= 1) return;
    const additionalIds = packages.slice(1).map((p) => p.id);
    const allAdditionalSelected = additionalIds.every((id) => selectedIds.has(id));

    if (allAdditionalSelected) {
      // Keep only bestMatch selected
      setSelectedIds(new Set([packages[0].id]));
    } else {
      setSelectedIds(new Set(packages.map((p) => p.id)));
    }
  };

  useEffect(() => {
    const fetchPackageById = async (id: string) => {
      try {
        setStatus('loading');
        setIsLoading(true);
        setError(null);

        const response = await HttpService.get(`${API_CONFIG.path.outboundFlow}/${id}`);

        const pkgData = response.data || response;

        if (pkgData && pkgData.id) {
          let finalPackages = [pkgData];

          // Try to fetch other packages for the same receiver
          const urlSearchQuery = searchParams.get('searchQuery');
          const receiverName = urlSearchQuery || pkgData.packageMetadata?.receiver?.name;
          if (receiverName) {
            try {
              const searchRes = await HttpService.get(API_CONFIG.path.packageList, {
                order: 'asc',
                pageSize: '-1',
                searchQuery: receiverName,
                sortBy: 'createdAt',
              });

              // Depending on API response structure, extract the array
              const searchData = searchRes.data || searchRes;
              const packagesArray = Array.isArray(searchData) ? searchData : searchData.data || [];

              if (Array.isArray(packagesArray)) {
                // Filter out the primary package
                const otherPackages = packagesArray.filter((p: PackageData) => p.id !== pkgData.id);
                finalPackages = [pkgData, ...otherPackages];
              }
            } catch (searchError) {
              console.error('Error fetching same receiver packages:', searchError);
            }
          }

          setPackages(finalPackages);
          if (finalPackages.length > 0) {
            setSelectedIds(new Set([finalPackages[0].id]));
          }
          setStatus('found');

          addScanToHistory({
            trackingId: pkgData.packageNumber || 'Unknown',
            slot: pkgData.slotCode,
            status: 'retrieved',
            type: 'outbound',
          });
        } else {
          setStatus('not_found');
          setError(t('no_package_found_image'));
        }
      } catch (error) {
        console.error('Error fetching package data by ID:', error);
        const err = error as { response?: { status?: number } };
        if (err.response && err.response.status === 404) {
          setStatus('not_found');
          setError(t('no_package_found_image'));
        } else {
          setStatus('error');
          setError(t('error'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    const packageDataFromState = location.state?.packageData;

    if (packageDataFromState) {
      const actualData = packageDataFromState.data || packageDataFromState;
      const pkgs = actualData.bestMatch
        ? [actualData.bestMatch, ...(actualData.sameReceiverPackages || [])]
        : [actualData];

      setPackages(pkgs);
      if (pkgs.length > 0) {
        if (location.state?.preselectAll) {
          setSelectedIds(new Set(pkgs.map((p: any) => p.id)));
        } else {
          setSelectedIds(new Set([pkgs[0].id]));
        }
      }
      setStatus('found');
      setIsLoading(false);

      if (pkgs.length > 0) {
        addScanToHistory({
          trackingId: pkgs[0].packageNumber || 'Unknown',
          slot: pkgs[0].slotCode,
          status: 'retrieved',
          type: 'outbound',
        });
      }
    } else if (packageIdParam) {
      fetchPackageById(packageIdParam);
    } else if (manualInputParam === 'true') {
      setStatus('manual_input');
      setError(t('scanner_timeout_manual_entry'));
    } else if (searchParams.has('imageUrl')) {
      setStatus('not_found');
      setError(t('no_package_found_image'));
    }
  }, [packageIdParam, manualInputParam, searchParams, location.state, t]);

  // Handle successful image scan from scanner
  const handleScanSuccess = useCallback(
    async (imageUrl: string, packageData?: any | null) => {
      // If package data is provided directly (from manual search or scanner), use it immediately
      if (packageData) {
        // Get current status using functional update to avoid closure issues
        setStatus('found');

        const actualData = packageData.data || packageData;
        // Check if packageData contains bestMatch (from API)
        const pkgs = actualData.bestMatch
          ? [actualData.bestMatch, ...(actualData.sameReceiverPackages || [])]
          : [actualData];

        setPackages(pkgs);
        if (pkgs.length > 0) {
          setSelectedIds(new Set([pkgs[0].id]));
        }
        setIsLoading(false); // Set loading to false immediately

        // Add to history
        if (pkgs.length > 0) {
          addScanToHistory({
            trackingId: pkgs[0].packageNumber || 'Unknown',
            slot: pkgs[0].slotCode,
            status: 'retrieved',
            type: 'outbound',
          });
        }
        return;
      } else {
        setStatus('not_found');
        setError(t('no_package_found_image'));
      }
    },
    [t],
  );

  // Handle scanner errors
  const handleScannerError = useCallback((error: string) => {
    console.error('Scanner error:', error);
    setError(error);
    setStatus('error');
  }, []);

  // API call to fetch package by name
  const fetchPackageByName = async (name: string) => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await HttpService.get(`${API_CONFIG.path.outboundFlow}?searchQuery=${encodeURIComponent(name)}`);

      if (response.data && response.data.length > 0) {
        setPackages(response.data);
        if (response.data.length > 0) {
          setSelectedIds(new Set([response.data[0].id]));
        }
        setStatus('found');

        // Add to history
        const foundPackage = response.data[0];
        addScanToHistory({
          trackingId: foundPackage.packageNumber,
          slot: foundPackage.slotCode,
          status: 'retrieved',
          type: 'outbound',
        });
      } else {
        setError(t('no_package_found_name'));
        setStatus('not_found');
      }
    } catch (err) {
      console.error(err);
      setError(t('failed_to_search_by_name'));
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  // API call to pick package
  const handlePickSelected = async () => {
    try {
      setIsPicking(true);
      setError(null);
      let successCount = 0;

      for (const id of selectedIds) {
        try {
          await HttpService.put(`${API_CONFIG.path.outboundFlow}/${id}/pick`, { isReturn }, { skipNotify: true });
          successCount++;

          const pkg = packages.find((p) => p.id === id);
          if (pkg) {
            addScanToHistory({
              trackingId: pkg.packageNumber,
              slot: pkg.slotCode,
              status: 'picked',
              type: 'outbound',
            });
          }
        } catch {
          // ignore individual failures for now, keep picking
        }
      }

      if (successCount > 0) {
        setStatus('picked');
      } else {
        setError(t('failed_to_pick_package'));
        setStatus('error');
      }
    } catch (err) {
      console.error('Error picking package:', err);
      setError(t('failed_to_pick_package'));
      setStatus('error');
    } finally {
      setIsPicking(false);
    }
  };

  // Handle manual name submission
  const handleManualNameSubmit = () => {
    if (manualName.trim()) {
      fetchPackageByName(manualName.trim());
    }
  };

  // Recover to 'found' only while still scanning/loading. Without this check a
  // 404 gets overwritten back to 'found' when older packages are still in state.
  useEffect(() => {
    if (packages.length > 0 && (status === 'scanning' || status === 'loading')) {
      setStatus('found');
    }
  }, [packages, status]);

  useEffect(() => {
    if (status === 'not_found') {
      Sentry.captureMessage('Outbound Package/Receiver Not Found', {
        level: 'warning',
        tags: {
          flow: 'outbound',
          error_type: 'not_found',
        },
        extra: {
          packageIdParam,
          manualName,
          error,
          searchQuery: searchParams.get('searchQuery'),
        },
      });
    }
  }, [status, packageIdParam, manualName, error, searchParams]);

  const statusConfig = {
    scanning: {
      icon: <Loader2 className="w-10 h-10 animate-spin" />,
      label: t('processing_scan'),
      className: 'bg-blue-500 text-white',
      badgeClass: 'bg-blue-100 text-blue-800 border-blue-400',
    },
    loading: {
      icon: <Loader2 className="w-10 h-10 animate-spin" />,
      label: t('searching'),
      className: 'bg-blue-500 text-white',
      badgeClass: 'bg-blue-100 text-blue-800 border-blue-400',
    },
    found: {
      icon: <Check className="w-10 h-10" />,
      label: t('package_found'),
      className: isReturn ? 'bg-amber-500 text-white' : 'bg-success text-success-foreground',
      badgeClass: isReturn ? 'bg-amber-100 text-amber-800 border-amber-400' : 'status-ready',
    },
    not_found: {
      icon: <X className="w-10 h-10" />,
      label: t('not_found'),
      className: 'bg-destructive text-destructive-foreground',
      badgeClass: 'status-error',
    },
    manual_input: {
      icon: <AlertTriangle className="w-10 h-10" />,
      label: t('manual_entry_required'),
      className: 'bg-warning text-warning-foreground',
      badgeClass: 'status-warning',
    },
    error: {
      icon: <X className="w-10 h-10" />,
      label: t('error'),
      className: 'bg-destructive text-destructive-foreground',
      badgeClass: 'status-error',
    },
    picked: {
      icon: <Check className="w-10 h-10" />,
      label: isReturn ? t('package_returned') : t('package_picked'),
      className: isReturn ? 'bg-amber-500 text-white' : 'bg-success text-success-foreground',
      badgeClass: isReturn ? 'bg-amber-100 text-amber-800 border-amber-400' : 'status-ready',
    },
  };

  const config = statusConfig[status];

  return (
    <div className="min-h-[calc(100vh-73px)] bg-background flex flex-col items-center justify-center p-6 relative">
      <div className="absolute top-6 left-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
      </div>

      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-center max-w-md w-full"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.1, type: 'spring' }}
          className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${config.className}`}
        >
          {config.icon}
        </motion.div>

        <div
          className={`inline-flex items-center px-4 py-2 rounded-full border text-sm font-semibold mb-4 ${config.badgeClass}`}
        >
          {config.label}
        </div>

        {/* Scanner for scanning state */}
        {status === 'scanning' && (
          <div className="mb-6">
            <OutboundScanner onScanSuccess={handleScanSuccess} onError={handleScannerError} />
          </div>
        )}

        {/* Package found display */}
        {status === 'found' && packages.length > 0 && (
          <div className="mb-6 space-y-4">
            {/* Best Match UI */}
            <div
              className={`p-4 rounded-lg text-left cursor-pointer transition-colors border ${
                isReturn
                  ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                  : 'bg-green-50 border-green-200 hover:bg-green-100'
              }`}
              onClick={() => setSelectedPackage(packages[0])}
            >
              <div className="flex items-center gap-3 mb-3">
                <Package className={`w-8 h-8 ${isReturn ? 'text-amber-600' : 'text-green-600'}`} />
                <div>
                  <p className={`font-semibold ${isReturn ? 'text-amber-800' : 'text-green-800'}`}>
                    {t('package_details')}
                  </p>
                  <p className={`text-sm ${isReturn ? 'text-amber-600' : 'text-green-600'}`}>
                    {isReturn ? t('ready_for_return') : t('ready_for_pickup')}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div
                  className={`flex items-center justify-between p-2 bg-white rounded border ${isReturn ? 'border-amber-100' : 'border-green-100'}`}
                >
                  <span className={`text-sm font-medium ${isReturn ? 'text-amber-800' : 'text-green-800'}`}>
                    {t('package_number')}
                  </span>
                  <span className={`font-mono text-sm font-bold ${isReturn ? 'text-amber-900' : 'text-green-900'}`}>
                    {packages[0].packageNumber}
                  </span>
                </div>
                <div
                  className={`flex items-center justify-between p-2 bg-white rounded border ${isReturn ? 'border-amber-100' : 'border-green-100'}`}
                >
                  <span className={`text-sm font-medium ${isReturn ? 'text-amber-800' : 'text-green-800'}`}>
                    {t('slot_location')}
                  </span>
                  <div className="flex items-center gap-1">
                    <MapPin className={`w-4 h-4 ${isReturn ? 'text-amber-600' : 'text-green-600'}`} />
                    <span
                      className={`font-mono text-sm font-bold px-1.5 py-0.5 rounded ${isReturn ? 'text-amber-700 bg-amber-100' : 'text-green-700 bg-green-100'}`}
                    >
                      {[packages[0].frameNumber, packages[0].boxNumber, packages[0].storageOption]
                        .filter(Boolean)
                        .join(', ') || t('na')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Packages Section */}
            {packages.length > 1 && (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-left mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-800">{t('more_matched_parcels')}</p>
                    <p className="text-xs text-gray-500">{t('select_additional_pickup')}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs bg-white shrink-0"
                    onClick={handleSelectAll}
                  >
                    <CheckSquare className="w-4 h-4 mr-1" />
                    {packages.slice(1).every((p) => selectedIds.has(p.id)) ? t('deselect_all') : t('select_all')}
                  </Button>
                </div>

                <div className="space-y-2 max-h-[30vh] overflow-y-auto pr-1">
                  {packages.slice(1).map((pkg) => {
                    const isSelected = selectedIds.has(pkg.id);
                    return (
                      <div
                        key={pkg.id}
                        onClick={() => setSelectedPackage(pkg)}
                        className={`flex flex-col gap-2 p-3 rounded-lg border transition-colors cursor-pointer ${
                          isSelected
                            ? 'bg-white border-primary shadow-sm'
                            : 'bg-white/60 border-gray-200 hover:bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Checkbox */}
                          <button
                            type="button"
                            aria-label={isSelected ? t('deselect_package') : t('select_package')}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelect(pkg.id);
                            }}
                            className="p-2 -m-2 flex items-center justify-center shrink-0"
                          >
                            <span
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                                isSelected
                                  ? 'bg-primary border-primary text-white'
                                  : 'bg-white border-gray-300 hover:border-primary'
                              }`}
                            >
                              {isSelected && <CheckSquare className="w-3.5 h-3.5" />}
                            </span>
                          </button>
                          {/* <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelect(pkg.id);
                            }}
                            className={`w-5 h-5 rounded border flex items-center justify-center transition-colors shrink-0 ${
                              isSelected ? 'bg-primary border-primary text-white' : 'bg-white border-gray-300'
                            }`}
                          >
                            {isSelected && <CheckSquare className="w-3.5 h-3.5" />}
                          </button> */}
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Package className="w-4 h-4 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-bold calculator-font text-foreground text-lg">
                              {pkg.packageNumber || 'N/A'}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {pkg.packageMetadata?.receiver?.name || t('na')}
                            </p>
                          </div>
                          {pkg.packageMetadata?.carrier && (
                            <span
                              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${
                                carrierStyle(pkg.packageMetadata.carrier).bg
                              } ${carrierStyle(pkg.packageMetadata.carrier).border} ${
                                carrierStyle(pkg.packageMetadata.carrier).text
                              }`}
                            >
                              {pkg.packageMetadata.carrier}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 pl-9">
                          <div className="flex items-center gap-1 text-xs">
                            <MapPin className={`w-3 h-3 ${isReturn ? 'text-amber-500' : 'text-green-500'}`} />
                            <span
                              className={`font-mono font-bold px-1.5 py-0.5 rounded ${isReturn ? 'text-amber-700 bg-amber-100' : 'text-green-700 bg-green-100'}`}
                            >
                              {[pkg.frameNumber, pkg.boxNumber, pkg.storageOption].filter(Boolean).join(', ') ||
                                t('na')}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manual input field */}
        {status === 'manual_input' && (
          <div className="flex flex-col items-center justify-center p-4">
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-lg shadow-md max-w-md w-full">
              <div className="flex items-center mb-4">
                <AlertTriangle className="text-yellow-500 text-3xl mr-3" />
                <h3 className="text-xl font-semibold">{t('manual_entry_required')}</h3>
              </div>
              <p className="text-sm mb-4">{t('we_couldnt_find_any')}</p>
              <div className="flex flex-col gap-3">
                <input
                  type="text"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder={t('enter_package_receivers_name')}
                  className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && handleManualNameSubmit()}
                />
                <Button
                  onClick={handleManualNameSubmit}
                  disabled={!manualName.trim() || isLoading}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded-lg"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : null}
                  {t('search_by_name')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setManualName('');
                    setError(null);
                    navigate('/scan?mode=outbound');
                  }}
                  disabled={isLoading}
                  className="w-full text-yellow-700 border-yellow-500 hover:bg-yellow-50"
                >
                  {t('cancel')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Package picked success */}
        {status === 'picked' && packages.length > 0 && (
          <div className="mb-6">
            <div
              className={`p-4 rounded-lg border ${isReturn ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}
            >
              <p className={`font-semibold mb-2 ${isReturn ? 'text-amber-800' : 'text-green-800'}`}>
                {isReturn ? '↩️' : '✅'} {isReturn ? t('successfully_returned') : t('successfully_picked')}
              </p>
              <div className={`space-y-1 text-sm ${isReturn ? 'text-amber-700' : 'text-green-700'}`}>
                {packages
                  .filter((p) => selectedIds.has(p.id))
                  .map((pkg) => (
                    <p key={pkg.id}>
                      {t('package')} <span className="font-bold">{pkg.packageNumber}</span> {t('from')}{' '}
                      {[pkg.frameNumber, pkg.boxNumber, pkg.storageOption].filter(Boolean).join(', ') || t('na')}
                    </p>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-3">
          {status === 'found' && packages.length > 0 && (
            <Button
              variant="action"
              size="xxl"
              className={`w-full h-12 ${isReturn ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
              onClick={handlePickSelected}
              disabled={isPicking || selectedIds.size === 0}
            >
              {isPicking ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
              {isReturn ? t('return_package') : t('pick_package')}{' '}
              {packages.length > 1 && selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
            </Button>
          )}

          {status === 'picked' && (
            <>
              <Button variant="action" size="xxl" className="w-full h-12" onClick={() => navigate('/')}>
                {t('done')}
              </Button>
              <Button
                variant="outline"
                size="xl"
                className="w-full h-12"
                onClick={() => navigate('/scan?mode=outbound')}
              >
                {t('scan_another')}
              </Button>
            </>
          )}

          {(status === 'not_found' || status === 'error') && (
            <div className="space-y-3">
              {error?.includes('timeout') && (
                <Button variant="action" size="xl" className="w-full" onClick={() => navigate('/scan?mode=outbound')}>
                  <Loader2 className="w-4 h-4 mr-2" />
                  {t('retry_scanning')}
                </Button>
              )}
              <Button variant="outline" size="xl" className="w-full" onClick={() => navigate('/scan?mode=outbound')}>
                {t('scan_again')}
              </Button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Details Modal */}
      <PackageDetailsModal
        isOpen={isModalOpen}
        onClose={() => setSelectedPackage(null)}
        pkg={selectedPackage}
        isEditing={false}
        actionButtonText={
          selectedPackage && selectedIds.has(selectedPackage.id) ? t('deselect_package') : t('select_package')
        }
        onActionClick={(e) => {
          e.stopPropagation();
          if (selectedPackage) {
            toggleSelect(selectedPackage.id);
          }
        }}
      />
    </div>
  );
};

export default OutboundFlow;
